import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ActionIcon, Avatar, Center, Divider, Group, Image, Stack, Text, Tooltip, UnstyledButton } from '@mantine/core';
import { useLocalStorage, useMediaQuery } from '@mantine/hooks';
import { IconCircleChevronLeft, IconCircleChevronRight, IconUserCircle, IconLogout, IconSwitchHorizontal, IconSettings } from '@tabler/icons-react';

import classes from "./NavigationBar.module.css";
import { AzureADRoles } from '../../Auth/auth-roles';
import { hasRole, useAccountRoles } from '../../Auth/auth-functions';

export const useNavigationBarState = () => {
	const isMobile = useMediaQuery('(max-width: 48em)');
	const [navigationOpened, setNavigationOpened] = useLocalStorage({ key: 'ord-navigation-opened', defaultValue: !isMobile });

	const toggle = () => {
		setNavigationOpened((prev) => !prev);
	};

	return {
		isOpen: navigationOpened,
		toggle,
		open: () => setNavigationOpened(true),
		close: () => setNavigationOpened(false),
	};
}

export interface NavigationItem {
	link: string;
	label: string;
	icon: React.FC<any>;
	divider?: boolean;
	roleRequired?: AzureADRoles;
}

interface Props { 
	title: string;
	logoPath?: string;
	items: NavigationItem[]; // Updated to use NavigationItem type
	userName?: string;
	onLogout?: () => void;
	onSwitchAccount?: () => void;
}

export const NavigationBar = ({ title, items, logoPath, userName, onLogout, onSwitchAccount }: Props) => {
	const navigate = useNavigate();
	const location = useLocation();
	const isMobile = useMediaQuery('(max-width: 48em)');
	const userRoles = useAccountRoles();

	const { isOpen: navigationOpened, toggle: toggleNavigation, close: closeNavigation } = useNavigationBarState();
	const [activePath, setActivePath] = useState(location.pathname);

	useEffect(() => {
		setActivePath(location.pathname);
		if (isMobile) {
			closeNavigation();
		}
	}, [location.pathname]);

	const links = items.filter(item => !item.roleRequired || hasRole(userRoles, item.roleRequired, false)).map((item, index) => item.divider ? <Divider key={index} my="sm" /> : (
		<Tooltip label={item.label} key={index} color="var(--mantine-primary-color-filled)" withArrow arrowSize={10} position="right" radius="sm" disabled={navigationOpened}>
		<Link to={item.link}
			className={`${classes.link} ${!navigationOpened ? classes.collapsed : ''}`}
			data-active={(activePath == "/" && item.link == "/") || (item.link.length > 1 && activePath.startsWith(item.link) || item.link == "/users" && activePath.startsWith("/team")) ? true : undefined}
			key={item.label}
			onClick={() => {
				setActivePath(item.link);
				if (isMobile)
					closeNavigation();
			}}
		>
			<item.icon className={classes.linkIcon} stroke={1.5} />
			{navigationOpened &&
			<span>{item.label}</span>
			}
		</Link>
		</Tooltip>
	));

	const handlePreferences = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
		event.preventDefault();
		if (isMobile)
			closeNavigation();

		navigate("/preferences");
	}

	const handleSwitchAccount = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
		event.preventDefault();

		if (onSwitchAccount) 
			onSwitchAccount();
	}

	const handleLogout = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
		event.preventDefault();
		if (onLogout) 
			onLogout();
	}

	return <>
		<nav className={classes.navbar}>
			{/* <Stack hiddenFrom="sm" align='center' py="md" mt="60" gap={0}> */}
			<Stack align="center" hiddenFrom="sm" py="md" mt={60} gap={0}>
				<Avatar size="xl" mb="sm" />
				<Text size="xl">{userName}</Text>
				{/* <TeamToggle /> */}
				<Divider my="sm" w="100%" />
			</Stack>
			{/* <Group spacing="xs" visibleFrom="xs" mb="lg" ml="xs" justify="start" wrap="nowrap"> */}
			{!navigationOpened && <>
				<Center mb={28}>
					<Tooltip label="Expand Navigation" color="var(--mantine-primary-color-filled)" withArrow arrowSize={10} position="right" radius="sm">
						<ActionIcon radius="xl" variant="subtle" color="indigo.3" bg="var(--mantine-color-body" mt={3} onClick={toggleNavigation}>{navigationOpened ? <IconCircleChevronLeft /> : <IconCircleChevronRight /> }</ActionIcon>
					</Tooltip>
				</Center>
			</>}
			{navigationOpened &&
				<Group gap="xs" mb="lg" ml={0} visibleFrom="sm" align="start" wrap="nowrap">
					<Avatar><Image src={logoPath} /></Avatar>				
					<Text fw="bold" size="18px">{title}</Text>
				</Group>
			}

			<div className={classes.navbarMain}>
				{links}
			</div>

			<div className={classes.footer}>
				<Stack align="left" gap={navigationOpened ? 0 : 8}>
					{navigationOpened && userName && <>
						<Group wrap="nowrap" gap={0} align="start" pl={7} className={classes.linkspace} visibleFrom="sm">
							<IconUserCircle className={classes.linkIcon} stroke={1.5} />
							<Text fz={isMobile ? "xl" : "sm"} truncate>{userName}</Text>
						</Group>
					</>}
					{!navigationOpened && userName &&
					<Tooltip label={userName} color="var(--mantine-primary-color-filled)" withArrow arrowSize={10} position="right" radius="sm" disabled={navigationOpened}>
					<UnstyledButton className={classes.link}>
						<IconUserCircle className={classes.linkIcon} stroke={1.5} />
						{navigationOpened &&
						<Text fz={isMobile ? "xl" : "sm"} fw="500">{userName}</Text>
						}
					</UnstyledButton>
					</Tooltip>
					}

					<Tooltip label="Preferences" color="var(--mantine-primary-color-filled)" withArrow arrowSize={10} position="right" radius="sm" disabled={navigationOpened}>
					<UnstyledButton className={classes.link} onClick={(event) => handlePreferences(event)}>
						<IconSettings className={classes.linkIcon} stroke={1.5} />
						{navigationOpened &&
						<Text fz={isMobile ? "xl" : "sm"} fw="500">Preferences</Text>
						}
					</UnstyledButton>
					</Tooltip>
					
					{onSwitchAccount && <>
					<Tooltip label="Switch Account" color="var(--mantine-primary-color-filled)" withArrow arrowSize={10} position="right" radius="sm" disabled={navigationOpened}>
					<UnstyledButton className={classes.link} onClick={(event) => handleSwitchAccount(event)}>
						<IconSwitchHorizontal className={classes.linkIcon} stroke={1.5} />
						{navigationOpened &&
						<Text fz={isMobile ? "xl" : "sm"} fw="500">Switch Account</Text>
						}
					</UnstyledButton>
					</Tooltip>
					</>}

					<Tooltip label="Sign Out" color="var(--mantine-primary-color-filled)" withArrow arrowSize={10} position="right" radius="sm" disabled={navigationOpened}>
					<UnstyledButton className={classes.link} onClick={(event) => handleLogout(event)}>
						<IconLogout className={classes.linkIcon} stroke={1.5} />
						{navigationOpened &&
						<Text fz={isMobile ? "xl" : "sm"} fw="500">Sign Out</Text>
						}
					</UnstyledButton>
					</Tooltip>
				</Stack>
			</div>
		</nav>
	</>
}